import { Modal, Form, Input, Button, message, Select } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { API } from "../../../config/API";
import axios from "../../../config/axiosInstance";
import { countryHasZones, zonesForCountry } from "../../../config/zones";
import {
  DEFAULT_COUNTRY,
  activeCountries,
  getCountry,
  normalizeCountry,
} from "../../../config/countries";
import { useEpaperConfig } from "../../../config/epaperConfig";
import DialCodeInput, {
  dialCodeRule,
  normalizePhoneDigits,
  phoneNumberRule,
  splitSavedPhone,
} from "./dialCodeInput";

interface UserData {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  role?: string | null;
  register_mode?: string;
  amount?: string | null;
  zone?: string | null;
  country?: string | null;
}

interface UpdateModalProps {
  open: boolean;
  refresh: () => void;
  close: () => void;
  userData: UserData | null;
}

const UpdateModal = (props: UpdateModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { userData } = props;
  const configVersion = useEpaperConfig(props?.open);
  const selectedCountry: string =
    Form.useWatch("country", form) || DEFAULT_COUNTRY;
  const hasZones = countryHasZones(selectedCountry);
  const savedCountry = normalizeCountry(userData?.country) || DEFAULT_COUNTRY;

  // Active countries, plus the user's saved country even if it was disabled.
  const countryOptions = activeCountries()
    .concat(
      activeCountries().some((c) => c.code === savedCountry)
        ? []
        : [getCountry(savedCountry)]
    )
    .map((c) => ({
      value: c.code,
      label: `${c.name}${c.isActive ? "" : " (inactive)"}`,
    }));

  // Active zones of the selected country, plus the user's saved zone when it
  // is no longer in that list (kept as-is unless changed).
  const zoneOptions = (() => {
    const list = zonesForCountry(selectedCountry).map((z) => ({
      value: z.name,
      label: `${z.name} (${z.code})`,
    }));
    const saved = userData?.zone;
    if (
      saved &&
      selectedCountry === savedCountry &&
      !list.some((z) => z.value === saved)
    ) {
      list.push({ value: saved, label: `${saved} (saved)` });
    }
    return list;
  })();

  // Countries with zones pick from their own list; others use the country
  // itself as the zone (the backend enforces the same rule).
  const handleCountryChange = (code: string) => {
    if (countryHasZones(code)) {
      const zone = form.getFieldValue("zone");
      if (zone && !zonesForCountry(code).some((z) => z.name === zone)) {
        form.setFieldsValue({ zone: undefined });
      }
    } else {
      form.setFieldsValue({ zone: getCountry(code).name });
    }
  };

  // Parse phone number to extract country code and phone number
  const parsePhoneNumber = (phone: string) => {
    if (!phone) return { countryCode: "+971", phoneNumber: "" };
    const split = splitSavedPhone(phone);
    if (split) return split;
    // Default to UAE if no match
    return { countryCode: "+971", phoneNumber: phone.replace(/^0+/, "") };
  };

  // Phone as loaded. An unchanged phone is saved exactly as stored and not
  // re-checked (as on the Add Agent form), so a legacy number doesn't block
  // an unrelated edit.
  const originalPhoneRef = useRef<{ raw: string; countryCode: string; phoneNumber: string } | null>(null);
  const isOriginalPhone = (dialCode: string | undefined, local: string) => {
    const original = originalPhoneRef.current;
    return !!original && dialCode === original.countryCode && local === original.phoneNumber;
  };

  // Set form values when modal opens with user data
  useEffect(() => {
    if (userData) {
      const { countryCode, phoneNumber } = parsePhoneNumber(userData.phone || "");
      originalPhoneRef.current = userData.phone
        ? { raw: userData.phone, countryCode, phoneNumber }
        : null;

      form.setFieldsValue({
        full_name: userData.full_name || "",
        email: userData.email || "",
        phone: phoneNumber,
        countryCode: countryCode,
        // Records created before the country field existed are UAE.
        country: normalizeCountry(userData.country) || DEFAULT_COUNTRY,
        zone: userData.zone || undefined,
      });
    } else {
      form.resetFields();
    }
    // Re-parse once the configured dial codes have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, form, configVersion]);

  const handleSubmit = async (values: any) => {
    if (!userData) return;
    
    setLoading(true);

    try {
      const new_phone = isOriginalPhone(values.countryCode, values.phone)
        ? originalPhoneRef.current!.raw
        : values.countryCode + values.phone;

      const payload = {
        data: {
          full_name: values.full_name,
          email: values.email,
          phone: new_phone,
          zone: values.zone || null,
          country: values.country || DEFAULT_COUNTRY,
          role: userData.role ?? null,
        },
      };

      const api = `${API.BASE_URL}epaper-users/${userData.id}`;

      await axios.put(api, payload);

      message.success("User successfully updated!");
      form.resetFields();
      props?.refresh();
      props?.close();
    } catch (err: any) {
      const data = err?.response?.data;
      message.error(data?.error?.message || "Update failed");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      width="95%"
      style={{ maxWidth: 600, top: 20 }}
      title="Edit Epaper User"
      open={props?.open}
      onCancel={() => props?.close()}
      footer={null}
    >
      <br />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          countryCode: "+971", // ✅ UAE Default
        }}
      >
        <Form.Item
          label="Full Name"
          name="full_name"
          rules={[{ required: true, message: "Full name is required" }]}
        >
          <Input allowClear placeholder="Enter full name" size="large" />
        </Form.Item>

        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: "Please select a country" }]}
        >
          <Select
            size="large"
            showSearch
            optionFilterProp="label"
            onChange={handleCountryChange}
            options={countryOptions}
          />
        </Form.Item>

        <Form.Item label="Phone Number" required>
          <Input.Group compact>
            <Form.Item
              name="countryCode"
              noStyle
              rules={[dialCodeRule]}
            >
              <DialCodeInput size="large" />
            </Form.Item>

            <Form.Item
              name="phone"
              noStyle
              dependencies={["countryCode"]}
              normalize={normalizePhoneDigits}
              rules={[phoneNumberRule(isOriginalPhone)]}
            >
              <Input
                allowClear
                style={{ width: "60%" }}
                size="large"
                placeholder="XXXXXXXXX"
              />
            </Form.Item>
          </Input.Group>
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input 
            allowClear 
            placeholder="Enter email" 
            size="large"
            disabled={false}
          />
        </Form.Item>

        <Form.Item
          label="Zone"
          name="zone"
          rules={[{ required: false }]}
        >
          <Select
            allowClear={hasZones}
            disabled={!hasZones}
            showSearch
            placeholder="Select zone"
            size="large"
            optionFilterProp="label"
            options={hasZones ? zoneOptions : []}
          />
        </Form.Item>

        <br />

        <Row>
          <Col md={6}>
            <Form.Item>
              <Button onClick={() => props?.close()} size="large" block>
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col md={6}>
            <Form.Item>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                block
                loading={loading}
              >
                Update
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default UpdateModal;

